/**
 * UploadPage - Main page for uploading files to datasets
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/useToast';
import {
  FileDropzone,
  DatasetSelector,
  FileList,
  UploadProgress,
} from '@/features/upload/components';
import {
  useCreateUploadJob,
  useCompleteUploadJob,
  useCancelUploadJob,
  useFileUpload,
  useUploadProgress,
} from '@/features/upload/hooks/useUpload';
import type { LocalFile, CreateDatasetRequest } from '@/features/upload/types/upload.types';
import { uploadApi } from '@/lib/api/upload';

type UploadStep = 'select' | 'uploading' | 'complete';

export const UploadPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  // State
  const [step, setStep] = useState<UploadStep>('select');
  const [datasetConfig, setDatasetConfig] = useState<CreateDatasetRequest | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<LocalFile[]>([]);
  const [uploadJobId, setUploadJobId] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Mutations
  const createUploadJob = useCreateUploadJob();
  const completeUploadJob = useCompleteUploadJob();
  const cancelUploadJob = useCancelUploadJob();
  const { uploadMultiple, uploadProgress, clearProgress } = useFileUpload();

  // Derived state
  const datasetName = datasetConfig?.name || '';

  // Progress tracking
  const { progress, isConnected } = useUploadProgress(uploadJobId, {
    onComplete: (event) => {
      if (event.status === 'completed') {
        setStep('complete');
        toast({
          title: 'Upload completed',
          description: `Successfully uploaded ${event.completed_files} files to ${datasetName}`,
        });
      } else if (event.status === 'failed') {
        setUploadError('Upload job failed. Please try again.');
      }
    },
    onError: (error) => {
      console.error('SSE connection error:', error);
    },
  });

  // Handle file selection
  const handleFilesSelected = useCallback((files: LocalFile[]) => {
    setSelectedFiles((prev) => [...prev, ...files]);
    setUploadError(null);
  }, []);

  // Handle file removal
  const handleRemoveFile = useCallback((fileId: string) => {
    setSelectedFiles((prev) => prev.filter((f) => f.id !== fileId));
  }, []);

  // Handle dataset configuration change
  const handleDatasetChange = useCallback((config: CreateDatasetRequest | null) => {
    setDatasetConfig(config);
    setUploadError(null);
  }, []);

  // File list with live progress merged in for display. This used to be
  // mirrored into `selectedFiles` state via an effect, but it's a pure
  // computed value from `selectedFiles` + `uploadProgress` — no state, no
  // effect needed. (`uploadProgress` isn't guaranteed referentially stable
  // across renders — e.g. a test double can hand back a fresh object each
  // call — so this must be a plain derivation, not a render-phase guard
  // keyed on its identity, which would never converge and loop forever.)
  const filesWithProgress = useMemo(
    () =>
      selectedFiles.map((file) => {
        const progress = uploadProgress[file.id];
        if (!progress) return file;

        return {
          ...file,
          uploadProgress: progress.progress,
          status: progress.status,
          error: progress.error,
        };
      }),
    [selectedFiles, uploadProgress]
  );

  // Start upload
  const handleStartUpload = useCallback(async () => {
    if (!datasetConfig) {
      setUploadError('Please select or create a dataset');
      return;
    }

    if (selectedFiles.length === 0) {
      setUploadError('Please select at least one file');
      return;
    }

    try {
      setStep('uploading');
      setUploadError(null);

      // Create the dataset if it's new (check if it exists first)
      try {
        const existingDataset = await uploadApi.getDatasetByName(datasetConfig.name);
        if (!existingDataset) {
          // Dataset doesn't exist, create it
          await uploadApi.createDataset(datasetConfig);
        }
      } catch (error: any) {
        // If 404, dataset doesn't exist - create it
        if (error.response?.status === 404) {
          await uploadApi.createDataset(datasetConfig);
        } else {
          throw error;
        }
      }

      // Create upload job
      const result = await createUploadJob.mutateAsync({
        dataset_name: datasetConfig.name,
        total_files: selectedFiles.length,
        validate_schema: true,
      });

      setUploadJobId(result.upload_job.id);

      // Upload files
      const uploadResults = await uploadMultiple(result.upload_job.id, selectedFiles);

      // Check for failures
      const failedUploads = uploadResults.filter((r) => !r.success);
      const successfulUploads = uploadResults.filter((r) => r.success);

      // Complete the upload job
      await completeUploadJob.mutateAsync({
        uploadJobId: result.upload_job.id,
        data: {
          status: failedUploads.length === 0 ? 'completed' : 'failed',
          error_message:
            failedUploads.length > 0
              ? `${failedUploads.length} files failed to upload`
              : undefined,
        },
      });

      if (failedUploads.length > 0) {
        setUploadError(
          `${failedUploads.length} of ${selectedFiles.length} files failed to upload`
        );
      } else {
        toast({
          title: 'Upload completed',
          description: `Successfully uploaded ${successfulUploads.length} files`,
        });
      }
    } catch (error: any) {
      console.error('Upload failed:', error);
      setUploadError(error.message || 'Failed to upload files');
      setStep('select');

      toast({
        title: 'Upload failed',
        description: error.message || 'An error occurred during upload',
        variant: 'destructive',
      });
    }
  }, [
    datasetConfig,
    selectedFiles,
    createUploadJob,
    uploadMultiple,
    completeUploadJob,
    toast,
  ]);

  // Cancel upload
  const handleCancelUpload = useCallback(async () => {
    if (!uploadJobId) return;

    try {
      await cancelUploadJob.mutateAsync(uploadJobId);
      setStep('select');
      setUploadJobId(null);
      clearProgress();

      toast({
        title: 'Upload cancelled',
        description: 'The upload has been cancelled',
      });
    } catch (error: any) {
      console.error('Failed to cancel upload:', error);
      toast({
        title: 'Failed to cancel',
        description: error.message || 'Could not cancel upload',
        variant: 'destructive',
      });
    }
  }, [uploadJobId, cancelUploadJob, clearProgress, toast]);

  // View dataset after completion
  const handleViewDataset = useCallback(() => {
    if (datasetName) {
      navigate(`/datasets/${datasetName}`);
    }
  }, [datasetName, navigate]);

  // Reset form
  const handleReset = useCallback(() => {
    setStep('select');
    setDatasetConfig(null);
    setSelectedFiles([]);
    setUploadJobId(null);
    setUploadError(null);
    clearProgress();
  }, [clearProgress]);

  const canStartUpload = datasetConfig && selectedFiles.length > 0 && !createUploadJob.isPending;

  return (
    <div className="container mx-auto py-8 space-y-8">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold">Upload Files</h1>
        <p className="text-muted-foreground">
          Upload data files to your lakehouse datasets
        </p>
      </div>

      {/* Upload Steps */}
      {step === 'select' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Step 1: Select Dataset</CardTitle>
              <CardDescription>
                Choose an existing dataset or create a new one
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DatasetSelector
                value={datasetConfig}
                onChange={handleDatasetChange}
                disabled={createUploadJob.isPending}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Step 2: Select Files</CardTitle>
              <CardDescription>
                Upload Parquet, CSV, or JSON files
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FileDropzone
                onFilesSelected={handleFilesSelected}
                disabled={createUploadJob.isPending}
              />

              <FileList
                files={filesWithProgress}
                onRemove={handleRemoveFile}
                disabled={createUploadJob.isPending}
              />
            </CardContent>
          </Card>

          {uploadError && (
            <Alert variant="destructive">
              <AlertDescription>{uploadError}</AlertDescription>
            </Alert>
          )}

          <div className="flex gap-3">
            <Button
              onClick={handleStartUpload}
              disabled={!canStartUpload}
              size="lg"
              className="flex-1"
            >
              {createUploadJob.isPending ? 'Starting Upload...' : 'Start Upload'}
            </Button>

            {selectedFiles.length > 0 && (
              <Button
                onClick={handleReset}
                variant="outline"
                size="lg"
                disabled={createUploadJob.isPending}
              >
                Reset
              </Button>
            )}
          </div>
        </div>
      )}

      {step === 'uploading' && (
        <UploadProgress
          uploadJob={progress || null}
          files={progress?.files}
          onCancel={handleCancelUpload}
          isConnected={isConnected}
        />
      )}

      {step === 'complete' && (
        <div className="space-y-6">
          <UploadProgress
            uploadJob={progress || null}
            files={progress?.files}
            onComplete={handleViewDataset}
            isConnected={isConnected}
          />

          <div className="flex gap-3">
            <Button onClick={handleViewDataset} size="lg" className="flex-1">
              View Dataset
            </Button>
            <Button onClick={handleReset} variant="outline" size="lg">
              Upload More Files
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
